import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FileText,
  Quote,
  Mail,
  Users,
  Video,
  Plus,
  Edit,
  Trash2,
  LogOut,
  Eye,
  EyeOff,
  Check,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_goofy-hypatia-3/artifacts/xykys01p_Omlight%20Logo%20PNG.png";

const AdminPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");

  // Data states
  const [blogPosts, setBlogPosts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [authorPosts, setAuthorPosts] = useState([]); // Blog posts written by author

  // Modal states
  const [blogDialogOpen, setBlogDialogOpen] = useState(false);
  const [testimonialDialogOpen, setTestimonialDialogOpen] = useState(false);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [authorPostDialogOpen, setAuthorPostDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem("omlight_token")}` },
  }), []);

  const fetchAllData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [postsRes, testimonialsRes, contactsRes, subscribersRes, mediaRes] = await Promise.all([
        axios.get(`${API}/blog`, headers),
        axios.get(`${API}/testimonials`, headers),
        axios.get(`${API}/contact`, headers),
        axios.get(`${API}/newsletter`, headers),
        axios.get(`${API}/media`, headers),
      ]);

      setBlogPosts(postsRes.data);
      setTestimonials(testimonialsRes.data);
      setContacts(contactsRes.data);
      setSubscribers(subscribersRes.data);
      
      // Separate media items by type
      const allMedia = mediaRes.data;
      const mediaOnly = allMedia.filter(m => m.item_type !== 'blog');
      const blogsOnly = allMedia.filter(m => m.item_type === 'blog');
      setMediaItems(mediaOnly);
      setAuthorPosts(blogsOnly);

      // Calculate stats
      setStats({
        posts: postsRes.data.length,
        testimonials: testimonialsRes.data.length,
        contacts: contactsRes.data.filter((c) => !c.read).length,
        subscribers: subscribersRes.data.filter((s) => s.subscribed !== false).length,
        mediaCount: mediaOnly.length,
        authorPostCount: blogsOnly.length,
      });
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      toast.error("Failed to load data");
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const token = sessionStorage.getItem("omlight_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    // Verify token and load data
    const verifyAndLoad = async () => {
      try {
        await axios.get(`${API}/auth/me`, getAuthHeaders());
        fetchAllData();
      } catch (error) {
        sessionStorage.removeItem("omlight_token");
        navigate("/admin/login");
      }
    };
    verifyAndLoad();
  }, [navigate, getAuthHeaders, fetchAllData]);

  const handleLogout = () => {
    sessionStorage.removeItem("omlight_token");
    navigate("/admin/login");
  };

  // Blog CRUD
  const handleBlogSubmit = async (formData) => {
    try {
      if (editingItem) {
        await axios.put(`${API}/blog/${editingItem.id}`, formData, getAuthHeaders());
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/blog`, formData, getAuthHeaders());
        toast.success("Post created");
      }
      setBlogDialogOpen(false);
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save post");
    }
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/blog/${id}`, getAuthHeaders());
      toast.success("Post deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  // Testimonial CRUD
  const handleTestimonialSubmit = async (formData) => {
    try {
      if (editingItem) {
        await axios.put(`${API}/testimonials/${editingItem.id}`, formData, getAuthHeaders());
        toast.success("Testimonial updated");
      } else {
        await axios.post(`${API}/testimonials`, formData, getAuthHeaders());
        toast.success("Testimonial created");
      }
      setTestimonialDialogOpen(false);
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save testimonial");
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await axios.delete(`${API}/testimonials/${id}`, getAuthHeaders());
      toast.success("Testimonial deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete testimonial");
    }
  };

  // Media CRUD
  const handleMediaSubmit = async (formData) => {
    try {
      // Ensure media type is set
      const dataWithType = { ...formData, item_type: 'media' };
      if (editingItem) {
        await axios.put(`${API}/media/${editingItem.id}`, dataWithType, getAuthHeaders());
        toast.success("Media item updated");
      } else {
        await axios.post(`${API}/media`, dataWithType, getAuthHeaders());
        toast.success("Media item created");
      }
      setMediaDialogOpen(false);
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save media item");
    }
  };

  // Author Post (Blog) CRUD
  const handleAuthorPostSubmit = async (formData) => {
    try {
      // Ensure blog type is set
      const dataWithType = { ...formData, item_type: 'blog' };
      if (editingItem) {
        await axios.put(`${API}/media/${editingItem.id}`, dataWithType, getAuthHeaders());
        toast.success("Blog post updated");
      } else {
        await axios.post(`${API}/media`, dataWithType, getAuthHeaders());
        toast.success("Blog post created");
      }
      setAuthorPostDialogOpen(false);
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save blog post");
    }
  };

  const handleDeleteAuthorPost = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await axios.delete(`${API}/media/${id}`, getAuthHeaders());
      toast.success("Blog post deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete blog post");
    }
  };

  const handleDeleteMedia = async (id) => {
    if (!window.confirm("Delete this media item?")) return;
    try {
      await axios.delete(`${API}/media/${id}`, getAuthHeaders());
      toast.success("Media item deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete media item");
    }
  };

  // Contact actions
  const handleMarkRead = async (id) => {
    try {
      await axios.put(`${API}/contact/${id}/read`, {}, getAuthHeaders());
      fetchAllData();
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/contact/${id}`, getAuthHeaders());
      toast.success("Message deleted");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" title="Back to Website">
              <img src={LOGO_URL} alt="Omlight - Click to go back to website" className="h-10 logo-blend hover:opacity-80 transition-opacity cursor-pointer" />
            </a>
            <span className="font-sans text-sm text-[#718096]">Admin Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="font-sans text-sm text-[#D4AF37] hover:text-[#B3932E] flex items-center gap-2 transition-colors">
              <ExternalLink className="w-4 h-4" />
              View Website
            </a>
            <Button
              variant="ghost"
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="font-sans text-sm text-[#718096] hover:text-[#1A202C]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-[#E2E8F0] mb-8 h-auto p-1 flex-wrap">
            <TabsTrigger value="dashboard" data-testid="admin-tab-dashboard" className="font-sans text-sm">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="media" data-testid="admin-tab-media" className="font-sans text-sm">
              <Video className="w-4 h-4 mr-2" />
              Shared Media
            </TabsTrigger>
            <TabsTrigger value="reflections" data-testid="admin-tab-reflections" className="font-sans text-sm">
              <FileText className="w-4 h-4 mr-2" />
              Share A Reflection
            </TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="admin-tab-testimonials" className="font-sans text-sm">
              <Quote className="w-4 h-4 mr-2" />
              Testimonials
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="font-sans text-sm">
              <Mail className="w-4 h-4 mr-2" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="admin-tab-subscribers" className="font-sans text-sm">
              <Users className="w-4 h-4 mr-2" />
              Subscribers
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <h2 className="font-serif text-2xl text-[#1A202C] mb-6">Overview</h2>
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Shared Media" value={stats.mediaCount || 0} sublabel={`${mediaItems.filter(m => m.status === 'available').length} published`} />
                <StatCard label="Reflections" value={stats.authorPostCount || 0} sublabel={`${authorPosts.filter(p => p.status === 'available').length} published`} />
                <StatCard label="Testimonials" value={stats.testimonials} />
                <StatCard label="Subscribers" value={stats.subscribers} />
                <StatCard label="Unread Messages" value={stats.contacts} highlight />
              </div>
            )}
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-[#1A202C]">Blog Posts</h2>
              <Dialog open={blogDialogOpen} onOpenChange={setBlogDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="admin-add-blog-button"
                    onClick={() => setEditingItem(null)}
                    className="btn-gold font-sans text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    New Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="font-serif">
                      {editingItem ? "Edit Post" : "New Post"}
                    </DialogTitle>
                  </DialogHeader>
                  <BlogForm
                    initialData={editingItem}
                    onSubmit={handleBlogSubmit}
                    onCancel={() => {
                      setBlogDialogOpen(false);
                      setEditingItem(null);
                    }}
                  />
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {blogPosts.map((post) => (
                <div
                  key={post.id}
                  data-testid={`admin-blog-item-${post.id}`}
                  className="bg-white border border-[#E2E8F0] p-4 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-sans font-medium text-[#1A202C]">{post.title}</h3>
                      {post.published ? (
                        <span className="tag tag-gold">Published</span>
                      ) : (
                        <span className="tag">Draft</span>
                      )}
                    </div>
                    <p className="font-sans text-sm text-[#718096]">/{post.slug}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingItem(post);
                        setBlogDialogOpen(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteBlog(post.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {blogPosts.length === 0 && (
                <p className="text-center py-8 text-[#718096]">No blog posts yet</p>
              )}
            </div>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-[#1A202C]">Testimonials</h2>
              <Dialog open={testimonialDialogOpen} onOpenChange={setTestimonialDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="admin-add-testimonial-button"
                    onClick={() => setEditingItem(null)}
                    className="btn-gold font-sans text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Testimonial
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-serif">
                      {editingItem ? "Edit Testimonial" : "New Testimonial"}
                    </DialogTitle>
                  </DialogHeader>
                  <TestimonialForm
                    initialData={editingItem}
                    onSubmit={handleTestimonialSubmit}
                    onCancel={() => {
                      setTestimonialDialogOpen(false);
                      setEditingItem(null);
                    }}
                  />
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  data-testid={`admin-testimonial-item-${t.id}`}
                  className="bg-white border border-[#E2E8F0] p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-sans font-medium text-[#1A202C]">{t.name}</h3>
                        {t.active ? (
                          <Eye className="w-4 h-4 text-green-500" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-[#718096]" />
                        )}
                      </div>
                      <p className="font-sans text-sm text-[#718096] mb-2">
                        {t.title} · {t.location}
                      </p>
                      <p className="font-sans text-sm text-[#4A5568] line-clamp-2">
                        "{t.quote}"
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingItem(t);
                          setTestimonialDialogOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTestimonial(t.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Media Tab - Shared Media (Links only) */}
          <TabsContent value="media">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-serif text-2xl text-[#1A202C]">Shared Media</h2>
                <p className="font-sans text-sm text-[#718096] mt-1">Share YouTube videos, shorts, or any social media links</p>
              </div>
              <Dialog open={mediaDialogOpen} onOpenChange={setMediaDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="admin-add-media-button"
                    onClick={() => setEditingItem(null)}
                    className="btn-gold font-sans text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Link
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg bg-white">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-[#1A202C]">
                      {editingItem ? "Edit Media" : "Add Media Link"}
                    </DialogTitle>
                  </DialogHeader>
                  <ShareMediaForm
                    initialData={editingItem}
                    onSubmit={handleMediaSubmit}
                    onCancel={() => {
                      setMediaDialogOpen(false);
                      setEditingItem(null);
                    }}
                  />
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {mediaItems.map((item) => (
                <div
                  key={item.id}
                  data-testid={`admin-media-item-${item.id}`}
                  className="bg-white border border-[#E2E8F0] p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    {item.video_url && (
                      <div className="w-20 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                        <img 
                          src={getYoutubeThumbnail(item.video_url)} 
                          alt="" 
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                    <div>
                      {item.title && <h3 className="font-sans font-medium text-[#1A202C] mb-1">{item.title}</h3>}
                      {item.video_url && (
                        <p className="font-sans text-xs text-[#718096] truncate max-w-md">{item.video_url}</p>
                      )}
                      <span className={`tag mt-2 ${item.status === "available" ? "tag-gold" : ""}`}>
                        {item.status === "available" ? "Published" : "Draft"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.video_url && (
                      <a href={item.video_url} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingItem(item);
                        setMediaDialogOpen(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteMedia(item.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              {mediaItems.length === 0 && (
                <p className="text-center py-8 text-[#718096]">No shared media yet. Click "Add Link" to share videos or social media content.</p>
              )}
            </div>
          </TabsContent>

          {/* Reflections Tab - Share A Reflection */}
          <TabsContent value="reflections">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-serif text-2xl text-[#1A202C]">Share A Reflection</h2>
                <p className="font-sans text-sm text-[#718096] mt-1">Write and share your thoughts, wisdom, and reflections</p>
              </div>
              <Dialog open={authorPostDialogOpen} onOpenChange={setAuthorPostDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="admin-add-reflection-button"
                    onClick={() => setEditingItem(null)}
                    className="btn-gold font-sans text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Reflection
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl bg-white">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-[#1A202C]">
                      {editingItem ? "Edit Reflection" : "Share A New Reflection"}
                    </DialogTitle>
                  </DialogHeader>
                  <ShareReflectionForm
                    initialData={editingItem}
                    onSubmit={handleAuthorPostSubmit}
                    onCancel={() => {
                      setAuthorPostDialogOpen(false);
                      setEditingItem(null);
                    }}
                  />
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {authorPosts.map((post) => (
                <div
                  key={post.id}
                  data-testid={`admin-reflection-item-${post.id}`}
                  className="bg-white border border-[#E2E8F0] p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-sans font-medium text-[#1A202C]">{post.title}</h3>
                        <span className={`tag ${post.status === "available" ? "tag-gold" : ""}`}>
                          {post.status === "available" ? "Published" : "Draft"}
                        </span>
                      </div>
                      {post.content && (
                        <p className="font-sans text-sm text-[#718096] line-clamp-2">
                          {post.content.substring(0, 150)}...
                        </p>
                      )}
                      <p className="font-sans text-xs text-[#A0AEC0] mt-2">
                        {new Date(post.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingItem(post);
                          setAuthorPostDialogOpen(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAuthorPost(post.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {authorPosts.length === 0 && (
                <p className="text-center py-8 text-[#718096]">No reflections yet. Click "Add New Reflection" to share your thoughts and wisdom.</p>
              )}
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <h2 className="font-serif text-2xl text-[#1A202C] mb-6">Messages</h2>
            <div className="space-y-4">
              {contacts.map((c) => (
                <div
                  key={c.id}
                  data-testid={`admin-contact-item-${c.id}`}
                  className={`bg-white border p-4 ${
                    c.read ? "border-[#E2E8F0]" : "border-[#D4AF37]"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-sans font-medium text-[#1A202C]">{c.name}</h3>
                        {!c.read && <span className="w-2 h-2 rounded-full bg-[#D4AF37]" />}
                      </div>
                      <p className="font-sans text-sm text-[#718096] mb-1">{c.email}</p>
                      <p className="font-sans text-sm font-medium text-[#4A5568] mb-2">
                        {c.subject}
                      </p>
                      <p className="font-sans text-sm text-[#4A5568]">{c.message}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {!c.read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleMarkRead(c.id)}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteContact(c.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
              {contacts.length === 0 && (
                <p className="text-center py-8 text-[#718096]">No messages yet</p>
              )}
            </div>
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers">
            <h2 className="font-serif text-2xl text-[#1A202C] mb-6">
              Newsletter Subscribers ({subscribers.filter((s) => s.subscribed).length})
            </h2>
            <div className="bg-white border border-[#E2E8F0]">
              <table className="w-full">
                <thead className="bg-[#F3F0E9]">
                  <tr>
                    <th className="text-left font-sans text-sm font-medium text-[#4A5568] p-4">
                      Email
                    </th>
                    <th className="text-left font-sans text-sm font-medium text-[#4A5568] p-4">
                      Status
                    </th>
                    <th className="text-left font-sans text-sm font-medium text-[#4A5568] p-4">
                      Subscribed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((s) => (
                    <tr key={s.id} className="border-t border-[#E2E8F0]">
                      <td className="font-sans text-sm text-[#1A202C] p-4">{s.email}</td>
                      <td className="p-4">
                        {s.subscribed ? (
                          <span className="tag tag-gold">Active</span>
                        ) : (
                          <span className="tag">Unsubscribed</span>
                        )}
                      </td>
                      <td className="font-sans text-sm text-[#718096] p-4">
                        {new Date(s.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {subscribers.length === 0 && (
                <p className="text-center py-8 text-[#718096]">No subscribers yet</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, sublabel, highlight }) => (
  <div className={`bg-white border p-6 ${highlight ? "border-[#D4AF37]" : "border-[#E2E8F0]"}`}>
    <p className="font-sans text-sm text-[#718096] mb-1">{label}</p>
    <p className={`font-serif text-3xl ${highlight ? "text-[#D4AF37]" : "text-[#1A202C]"}`}>
      {value}
    </p>
    {sublabel && <p className="font-sans text-xs text-[#718096] mt-1">{sublabel}</p>}
  </div>
);

// Blog Form Component
const BlogForm = ({ initialData, onSubmit, onCancel }) => {
  const [form, setForm] = useState(
    initialData || {
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      author: "Linda Gallagher",
      published: false,
      featured_image: "",
    }
  );

  const handleTitleChange = (e) => {
    const title = e.target.value;
    const slug = title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    setForm({ ...form, title, slug: initialData ? form.slug : slug });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="space-y-4"
    >
      <div>
        <Label className="font-sans text-sm">Title</Label>
        <Input
          value={form.title}
          onChange={handleTitleChange}
          required
          className="rounded-none"
        />
      </div>
      <div>
        <Label className="font-sans text-sm">Slug</Label>
        <Input
          value={form.slug}
          onChange={(e) => setForm({ ...form, slug: e.target.value })}
          required
          className="rounded-none"
        />
      </div>
      <div>
        <Label className="font-sans text-sm">Excerpt</Label>
        <Textarea
          value={form.excerpt}
          onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
          required
          rows={2}
          className="rounded-none"
        />
      </div>
      <div>
        <Label className="font-sans text-sm">Content</Label>
        <Textarea
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          required
          rows={8}
          className="rounded-none"
        />
      </div>
      <div>
        <Label className="font-sans text-sm">Featured Image URL (optional)</Label>
        <Input
          value={form.featured_image}
          onChange={(e) => setForm({ ...form, featured_image: e.target.value })}
          className="rounded-none"
        />
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={form.published}
          onCheckedChange={(checked) => setForm({ ...form, published: checked })}
        />
        <Label className="font-sans text-sm">Published</Label>
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="submit" className="btn-gold font-sans">
          {initialData ? "Update" : "Create"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Testimonial Form Component
const TestimonialForm = ({ initialData, onSubmit, onCancel }) => {
  const [form, setForm] = useState(
    initialData || {
      name: "",
      title: "",
      location: "",
      quote: "",
      active: true,
    }
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="space-y-4"
    >
      <div>
        <Label className="font-sans text-sm">Name</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
          className="rounded-none"
        />
      </div>
      <div>
        <Label className="font-sans text-sm">Title/Profession</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
          className="rounded-none"
        />
      </div>
      <div>
        <Label className="font-sans text-sm">Location</Label>
        <Input
          value={form.location}
          onChange={(e) => setForm({ ...form, location: e.target.value })}
          required
          className="rounded-none"
        />
      </div>
      <div>
        <Label className="font-sans text-sm">Quote</Label>
        <Textarea
          value={form.quote}
          onChange={(e) => setForm({ ...form, quote: e.target.value })}
          required
          rows={4}
          className="rounded-none"
        />
      </div>
      <div className="flex items-center gap-2">
        <Switch
          checked={form.active}
          onCheckedChange={(checked) => setForm({ ...form, active: checked })}
        />
        <Label className="font-sans text-sm">Active (visible on site)</Label>
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="submit" className="btn-gold font-sans">
          {initialData ? "Update" : "Create"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Media Form Component - Share Your Wisdom
const MediaForm = ({ initialData, onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    title: "",
    content: "",
    description: "",
    video_url: "",
    image_url: "",
    status: "available",
    order: 0,
  });

  // Update form when initialData changes (for editing)
  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title || "",
        content: initialData.content || "",
        description: initialData.description || "",
        video_url: initialData.video_url || "",
        image_url: initialData.image_url || "",
        status: initialData.status || "available",
        order: initialData.order || 0,
      });
    } else {
      // Reset form for new items
      setForm({
        title: "",
        content: "",
        description: "",
        video_url: "",
        image_url: "",
        status: "available",
        order: 0,
      });
    }
  }, [initialData]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="space-y-4"
    >
      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Title</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Enter a meaningful title for your reflection"
          required
          className="rounded-none mt-1 bg-white"
        />
      </div>
      
      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Content</Label>
        <Textarea
          value={form.content || ""}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="Share your wisdom, thoughts, or reflection..."
          rows={6}
          className="rounded-none mt-1 bg-white"
        />
        <p className="font-sans text-xs text-[#718096] mt-1">Write the full content of your reflection or wisdom to share</p>
      </div>

      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Short Preview Text</Label>
        <Input
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="e.g., Reflection · 01"
          className="rounded-none mt-1 bg-white"
        />
        <p className="font-sans text-xs text-[#718096] mt-1">This appears as a label on the card</p>
      </div>
      
      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Video Reference Link (optional)</Label>
        <Input
          value={form.video_url || ""}
          onChange={(e) => setForm({ ...form, video_url: e.target.value })}
          placeholder="https://www.youtube.com/watch?v=..."
          className="rounded-none mt-1 bg-white"
        />
        <p className="font-sans text-xs text-[#718096] mt-1">YouTube video or shorts link</p>
      </div>

      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Image URL (optional)</Label>
        <Input
          value={form.image_url || ""}
          onChange={(e) => setForm({ ...form, image_url: e.target.value })}
          placeholder="https://example.com/image.jpg"
          className="rounded-none mt-1 bg-white"
        />
        <p className="font-sans text-xs text-[#718096] mt-1">URL of an image to display with this reflection</p>
      </div>

      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Display Order</Label>
        <Input
          type="number"
          value={form.order}
          onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
          className="rounded-none mt-1 w-24 bg-white"
        />
        <p className="font-sans text-xs text-[#718096] mt-1">Lower numbers appear first</p>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Switch
          checked={form.status === "available"}
          onCheckedChange={(checked) =>
            setForm({ ...form, status: checked ? "available" : "draft" })
          }
        />
        <Label className="font-sans text-sm text-[#1A202C]">Publish (visible on site)</Label>
      </div>

      <div className="flex gap-3 pt-4 border-t border-[#E2E8F0]">
        <Button type="submit" className="btn-gold font-sans">
          {initialData ? "Update Reflection" : "Share Wisdom"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Helper function to get YouTube thumbnail
const getYoutubeThumbnail = (url) => {
  if (!url) return null;
  const shortMatch = url.match(/youtube\.com\/shorts\/([^?&]+)/);
  if (shortMatch) return `https://img.youtube.com/vi/${shortMatch[1]}/mqdefault.jpg`;
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^?&]+)/);
  if (watchMatch) return `https://img.youtube.com/vi/${watchMatch[1]}/mqdefault.jpg`;
  return null;
};

// Share Media Form - Simple link sharing (ONLY link required)
const ShareMediaForm = ({ initialData, onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    title: "",
    description: "",
    video_url: "",
    status: "available",
    order: 0,
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title || "",
        description: initialData.description || "",
        video_url: initialData.video_url || "",
        status: initialData.status || "available",
        order: initialData.order || 0,
      });
    } else {
      setForm({
        title: "",
        description: "",
        video_url: "",
        status: "available",
        order: 0,
      });
    }
  }, [initialData]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="space-y-4"
    >
      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Media Link *</Label>
        <Input
          value={form.video_url || ""}
          onChange={(e) => setForm({ ...form, video_url: e.target.value })}
          placeholder="Paste any video or photo link (YouTube, Facebook, Instagram...)"
          required
          className="rounded-none mt-1 bg-white"
        />
        <p className="font-sans text-xs text-[#718096] mt-1">YouTube shorts, videos, Facebook posts, Instagram reels - any media link works</p>
      </div>

      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Caption / Title (optional)</Label>
        <Textarea
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Add a reflective caption or quote..."
          rows={2}
          className="rounded-none mt-1 bg-white"
        />
      </div>

      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Label (optional)</Label>
        <Input
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="e.g., Reflection · 01"
          className="rounded-none mt-1 bg-white"
        />
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Switch
          checked={form.status === "available"}
          onCheckedChange={(checked) =>
            setForm({ ...form, status: checked ? "available" : "draft" })
          }
        />
        <Label className="font-sans text-sm text-[#1A202C]">Publish (visible on site)</Label>
      </div>

      <div className="flex gap-3 pt-4 border-t border-[#E2E8F0]">
        <Button type="submit" className="btn-gold font-sans">
          {initialData ? "Update" : "Add Media"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Share Reflection Form - Written content with optional media link
const ShareReflectionForm = ({ initialData, onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    title: "",
    content: "",
    video_url: "",
    image_url: "",
    status: "available",
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        title: initialData.title || "",
        content: initialData.content || "",
        video_url: initialData.video_url || "",
        image_url: initialData.image_url || "",
        status: initialData.status || "available",
      });
    } else {
      setForm({
        title: "",
        content: "",
        video_url: "",
        image_url: "",
        status: "available",
      });
    }
  }, [initialData]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(form);
      }}
      className="space-y-4"
    >
      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Title *</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Give your reflection a meaningful title"
          required
          className="rounded-none mt-1 bg-white"
        />
      </div>

      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Content *</Label>
        <Textarea
          value={form.content || ""}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="Share your thoughts, wisdom, and reflections..."
          required
          rows={8}
          className="rounded-none mt-1 bg-white"
        />
      </div>

      <div>
        <Label className="font-sans text-sm font-medium text-[#1A202C]">Link to Video or Image (optional)</Label>
        <Input
          value={form.video_url || form.image_url || ""}
          onChange={(e) => {
            const url = e.target.value;
            // Determine if it's an image or video based on URL
            if (url.match(/\.(jpg|jpeg|png|gif|webp)/i)) {
              setForm({ ...form, image_url: url, video_url: "" });
            } else {
              setForm({ ...form, video_url: url, image_url: "" });
            }
          }}
          placeholder="https://youtube.com/... or https://example.com/image.jpg"
          className="rounded-none mt-1 bg-white"
        />
        <p className="font-sans text-xs text-[#718096] mt-1">Add a related video or image to accompany your reflection</p>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Switch
          checked={form.status === "available"}
          onCheckedChange={(checked) =>
            setForm({ ...form, status: checked ? "available" : "draft" })
          }
        />
        <Label className="font-sans text-sm text-[#1A202C]">Publish (visible on site)</Label>
      </div>

      <div className="flex gap-3 pt-4 border-t border-[#E2E8F0]">
        <Button type="submit" className="btn-gold font-sans">
          {initialData ? "Update Reflection" : "Share Reflection"}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default AdminPage;
